import React from "react";

/*
 * Minimal route-planning illustration on a white background.
 * Pure SVG so it stays crisp, light and on-brand (indigo #4C30FF on white).
 */
const stops = [
  { x: 92, y: 250, n: 1 },
  { x: 178, y: 148, n: 2 },
  { x: 300, y: 118, n: 3 },
  { x: 402, y: 196, n: 4 },
  { x: 356, y: 312, n: 5 },
  { x: 230, y: 356, n: 6 },
];

const path =
  "M62 318 C 70 280, 80 262, 92 250 S 150 170, 178 148 S 260 112, 300 118 S 380 150, 402 196 S 380 290, 356 312 S 280 350, 230 356 S 110 350, 62 318";

const RouteMapIllustration = ({ className = "" }) => (
  <svg
    viewBox="0 0 480 400"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Optimized multi-stop delivery route on a map"
    data-testid="route-map-illustration"
  >
    <defs>
      <pattern id="rm-grid" width="24" height="24" patternUnits="userSpaceOnUse">
        <path d="M24 0H0V24" fill="none" stroke="#EEEEE9" strokeWidth="1" />
      </pattern>
      <filter id="rm-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0E0E10" floodOpacity="0.10" />
      </filter>
    </defs>

    {/* canvas */}
    <rect width="480" height="400" fill="#FFFFFF" />
    <rect width="480" height="400" fill="url(#rm-grid)" />

    {/* soft "streets" */}
    <g stroke="#E6E6E0" strokeWidth="10" strokeLinecap="round" fill="none">
      <path d="M0 210 H480" />
      <path d="M150 0 V400" />
      <path d="M340 0 V400" />
      <path d="M0 110 Q 240 90 480 130" />
      <path d="M0 330 Q 240 300 480 340" />
    </g>
    <g stroke="#F1F1EC" strokeWidth="4" strokeLinecap="round" fill="none">
      <path d="M60 0 V400" />
      <path d="M250 0 V400" />
      <path d="M430 0 V400" />
      <path d="M0 50 H480" />
      <path d="M0 270 H480" />
    </g>

    {/* unoptimised route (ghost) */}
    <path
      d="M62 318 L300 118 L92 250 L356 312 L178 148 L402 196 L230 356"
      fill="none"
      stroke="#D6D6CE"
      strokeWidth="2"
      strokeDasharray="4 6"
      opacity="0.8"
    />

    {/* optimised route */}
    <path d={path} fill="none" stroke="#EDEAFF" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d={path}
      fill="none"
      stroke="#4C30FF"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="1200"
      strokeDashoffset="1200"
      className="rm-route"
    />

    {/* depot */}
    <g transform="translate(62 318)" filter="url(#rm-shadow)">
      <rect x="-16" y="-16" width="32" height="32" rx="9" fill="#0E0E10" />
      <path d="M-7 3 V-3 L0 -8 L7 -3 V3 Z" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="-2.5" y="-1" width="5" height="4" fill="#FFFFFF" />
    </g>

    {/* stops */}
    {stops.map((s) => (
      <g key={s.n} transform={`translate(${s.x} ${s.y})`} filter="url(#rm-shadow)">
        <circle r="13" fill="#FFFFFF" stroke="#4C30FF" strokeWidth="2.5" />
        <text
          y="4.5"
          textAnchor="middle"
          fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
          fontSize="12"
          fontWeight="700"
          fill="#3A1FE0"
        >
          {s.n}
        </text>
      </g>
    ))}

    {/* moving vehicle on route */}
    <g filter="url(#rm-shadow)">
      <circle r="7" fill="#4C30FF">
        <animateMotion dur="9s" repeatCount="indefinite" path={path} />
      </circle>
      <circle r="12" fill="#4C30FF" opacity="0.18">
        <animateMotion dur="9s" repeatCount="indefinite" path={path} />
        <animate attributeName="r" values="9;16;9" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.25;0.05;0.25" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </g>

    {/* ETA card */}
    <g transform="translate(300 24)" filter="url(#rm-shadow)">
      <rect width="156" height="58" rx="12" fill="#FFFFFF" stroke="#E6E6E0" />
      <text x="14" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="9" fontWeight="600" fill="#6B6B73" letterSpacing="1.2">
        OPTIMIZED ROUTE
      </text>
      <text x="14" y="44" fontFamily="'Space Grotesk', Inter, sans-serif" fontSize="18" fontWeight="700" fill="#0E0E10">
        6 stops
      </text>
      <text x="82" y="44" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="12" fontWeight="600" fill="#3A1FE0">
        −28% km
      </text>
    </g>

    {/* time-window chip */}
    <g transform="translate(24 24)" filter="url(#rm-shadow)">
      <rect width="118" height="30" rx="15" fill="#0E0E10" />
      <circle cx="16" cy="15" r="4" fill="#25D366" />
      <text x="28" y="19" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="11" fontWeight="600" fill="#FFFFFF">
        On time · 09:40
      </text>
    </g>
  </svg>
);

export default RouteMapIllustration;
